import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import PageContainer from "../components/pages/PageContainer";
import SettingsPageHeading from "../components/pages/SettingsPageHeading";
import { sendHelpMessage } from "../api/user";
import ProcessingButton from "../components/Skeleton/ProcessingButton";

const TOPICS = [
  "General help",
  "Account issue",
  "Report a problem",
  "Privacy request",
  "Other",
];

// Best-effort OS / browser from the user agent — free info we already have, so
// we include it (no extra permissions or dev work per the client's note).
const getClientInfo = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  let os = "Unknown";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Macintosh|Mac OS X/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  let device = "Unknown";
  if (/Edg/i.test(ua)) device = "Edge";
  else if (/OPR|Opera/i.test(ua)) device = "Opera";
  else if (/Chrome/i.test(ua)) device = "Chrome";
  else if (/Firefox/i.test(ua)) device = "Firefox";
  else if (/Safari/i.test(ua)) device = "Safari";
  return { os, device };
};

const GetHelp = () => {
  const [sidebarToggle] = useOutletContext();
  const user = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const email = user?.email || "";

  const [topic, setTopic] = useState(TOPICS[0]); // General help — default selected
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const hasText = message.trim().length > 0;
  const canSend = hasText && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    const { os, device } = getClientInfo();
    // "Behind the scenes" — what support receives with the message.
    const payload = {
      full_name: user?.full_name || "", // if stored
      user_name: user?.user_name || "",
      email,
      phone: user?.phone || "", // if stored
      app_version: "2.9",
      device,
      os,
      timestamp: new Date().toISOString(),
      topic,
      message: message.trim(),
    };
    try {
      await sendHelpMessage(payload);
      toast.success("Message sent. We'll reply by email.");
      setMessage("");
      setTopic(TOPICS[0]);
    } catch (error) {
      toast.error("Could not send your message. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <DashboardHeader toggle={sidebarToggle} />
      {/* Settings sitewide: no desktop title/grey line, no mobile under-logo title. */}
      <PageHeaderTitle title="Get help" showDesktopTitle={false} />
      <PageContainer>
        <div className="px-4 pt-4 md:pt-10 md:px-0 md:max-w-[55rem] md:mx-auto">
          <SettingsPageHeading
            title="Get help"
            subtitle="Questions, feedback, or bugs"
            className="mb-3 md:mb-4"
          />
          <div className="md:rounded-2xl md:border md:border-gray-200 md:bg-white md:p-8">
            {/* Account line — where replies go. */}
            <p className="text-sm text-secondary">
              Replies go to{" "}
              <span className="font-medium text-gray-800">
                {email || "your account email"}
              </span>
            </p>

            {/* Fields share the form-field width cap on desktop (left-aligned);
                full-width on mobile. The textarea uses the same width — taller,
                not wider. */}
            <div className="md:max-w-form-field">
              {/* Topic */}
              <div className="mt-6">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Topic
                </label>
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-[#004c50] focus:outline-none focus:ring-1 focus:ring-[#004c50]"
                >
                  {TOPICS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder="How can we help?"
                  className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#004c50] focus:outline-none focus:ring-1 focus:ring-[#004c50]"
                />
              </div>
            </div>

            {/* Brand CTA — #004c50, Poppins. */}
            {/* `hasText` (not `canSend`) drives the fill, so sending keeps the
                brand colour — greying mid-send reads as "unavailable" rather
                than "busy". The ellipsis is gone per the label spec. */}
            <ProcessingButton
              onClick={handleSend}
              processing={sending}
              unavailable={!hasText && !sending}
              label="Send message"
              processingLabel="Sending"
              className={`mt-5 w-full rounded-lg px-5 py-2.5 font-poppins font-semibold text-white transition-colors md:w-auto ${
                hasText
                  ? "bg-[#004c50] hover:bg-[#003a3d]"
                  : "cursor-not-allowed bg-gray-300"
              }`}
            />
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default GetHelp;
